<script setup lang="ts">
import JobsBasicList from '@/components/basicLists/JobsBasicList.vue';
import KBtn from '@/components/btns/KBtn.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export type DialogType =
  | 'deallocate'
  | 'undo'
  | 'lock'
  | 'unlock'
  | 'cancel'
  | 'futile';

export interface Props {
  type?: DialogType;
  isConfirmLoading?: boolean;
  jobs?: { jobId: number; jobDate: string }[];
  confirm?: () => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  type: 'futile',
  isConfirmLoading: false,
  jobs: () => [],
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [event: Event, props: DefProps];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleConfirmClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, props);
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.dispatchConfirmDialog"
    data-testid="dispatchConfirmDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        <template v-if="props.type === 'deallocate'">
          Deallocate the Job
        </template>
        <template v-else-if="props.type === 'undo'">
          Undo All the Changes
        </template>
        <template v-else-if="props.type === 'lock'"> Lock the Job </template>
        <template v-else-if="props.type === 'unlock'">
          Unlock the Job
        </template>
        <template v-else-if="props.type === 'cancel'">
          Cancel the Job
        </template>
        <template v-else> Mark the Job as Futile </template>
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <JobsBasicList :jobs="props.jobs" class="mt-0 mb-6" />
      <div :class="$style.text">
        <template v-if="props.type === 'deallocate'">
          Are you sure you want to remove this job from the current driver?
        </template>
        <template v-else-if="props.type === 'undo'">
          Are you sure you want to undo all recent changes for this job?
        </template>
        <template v-else-if="props.type === 'lock'">
          Lock this job to prevent further changes?
        </template>
        <template v-else-if="props.type === 'unlock'">
          Unlock this job to allow further changes?
        </template>
        <template v-else-if="props.type === 'cancel'">
          This will cancel the job permanently. Are you sure?
        </template>
        <template v-else>
          This job will be marked as futile and removed from the active queue.
          Confirm?
        </template>
      </div>
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            <template v-if="props.type === 'cancel'"> Keep </template>
            <template v-else> Cancel </template>
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            autofocus
            @click="handleConfirmClick"
          >
            <template v-if="props.type === 'deallocate'"> Deallocate </template>
            <template v-else-if="props.type === 'undo'"> Undo all </template>
            <template v-else-if="props.type === 'lock'">
              Lock the job
            </template>
            <template v-else-if="props.type === 'unlock'">
              Unlock the job
            </template>
            <template v-else-if="props.type === 'cancel'">
              Cancel the job
            </template>
            <template v-else> Mark as futile </template>
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.dispatchConfirmDialog {
  width: 420px;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
